import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Refresh from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useMsal } from '@azure/msal-react';
import { API_BASE_URL } from '../config/api';
import { authFetch } from '../services/authFetch';
import apiClient from '../apiClient';

interface SpIncident {
  number: string;
  created_on: string | null;
  resolved_at: string | null;
  app_id: string;
  app_name: string;
  captured: boolean;
}

interface AllWkIncident {
  number: string;
  app_name: string;
  created_on: string | null;
  resolved_at: string | null;
  contact_type: string;
  caused_by: string;
  alert_captured: boolean;
  caused_by_change: boolean;
  ttr_seconds?: number | null;
  ttd_seconds?: number | null;
}

interface IncDrillData {
  label: string;
  start_date: string;
  end_date: string;
  mode: 'inc_count' | 'alert_capture' | 'caused_by_change' | 'mttd' | 'mttr';
  incidents: AllWkIncident[];
}

interface WeekData {
  week: number;
  week_label: string;
  start_date: string;
  end_date: string;
  incident_count: number;
  recurrence_count: number;
  avg_aging_days: number;
  rf_fullstack_pct: number | null;
  status_page_pct: number | null;
  status_page_incidents: SpIncident[];
  inc_raised_by_statuspage_pct?: number | null;
  inc_raised_by_statuspage_count?: number;
  inc_raised_by_statuspage_incidents?: SpIncident[];
  incidents_this_week?: Array<SpIncident & { raised?: boolean; captured?: boolean }>;
  raised_na?: boolean;
  alert_capture_pct?: number | null;
  alert_capture_count?: number;
  caused_by_change_pct?: number | null;
  caused_by_change_count?: number;
  avg_mttd_hrs?: number | null;
  avg_mttr_hrs?: number | null;
  all_wk_incidents?: AllWkIncident[];
  drill_mode?: 'capture' | 'raised';
  postmortem_incidents?: Array<{
    created_on: string | null;
    status: string | null;
    priority: string | null;
    recurring: boolean;
    incident_id?: string | null;
    recurrence?: { incidentId?: string | null } | null;
  }>;
}

const ValueChip: React.FC<{ value: any; suffix?: string }> = ({ value, suffix }) => {
  const display = value === null || value === undefined ? '—' : `${value}${suffix ?? ''}`;
  return (
    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
      {display}
    </Typography>
  );
};

// Statuspage Capture % data is only valid from this date onwards
const SP_CUTOFF = new Date('2026-03-11T00:00:00Z');

// LCP Grafana query — last_over_time with a 7-day window, step = 1 week
const LCP_GRAFANA_QUERY = 'last_over_time(unique_lcp_75:rate_5m{app="argus-app", app_name="Statuspage_V2-prod"}[604800s])';
// Anchor range query to Dec 28, 2025 (Sunday = last day of ISO W52 2025, one day before W1 2026 starts).
// Steps of 7d from a Sunday always land on Sundays (ISO week ends), so:
//   step 1 = Jan 4  (end of W1)  → last_over_time covers Dec 28–Jan 4  = W1  ✓
//   step 2 = Jan 11 (end of W2)  → covers Jan 4–11                     = W2  ✓
//   step 15 = Apr 12 (end of W15) → covers Apr 5–12                    = W15 ✓
const LCP_FETCH_START_UNIX = String(Math.floor(new Date('2025-12-28T00:00:00Z').getTime() / 1000));
const LCP_STEP = '604800'; // 7 days

const SrePage0Metrics: React.FC = () => {
  const { accounts } = useMsal();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [drillWeek, setDrillWeek] = useState<WeekData | null>(null);
  const [pmDrillWeek, setPmDrillWeek] = useState<WeekData | null>(null);
  const [incDrillData, setIncDrillData] = useState<IncDrillData | null>(null);
  // LCP data from Grafana: array of [unix_timestamp_seconds, value_string] pairs
  const [lcpGrafanaValues, setLcpGrafanaValues] = useState<Array<[number, string]>>([]);

  // Derived recurring postmortem rows for the PM drill modal (avoid IIFE in JSX)
  const pmRecurringRecs = pmDrillWeek?.postmortem_incidents?.filter(p => p.recurring) ?? [];

  const fetchLcpData = async () => {
    try {
      const nowUnix = String(Math.floor(Date.now() / 1000));
      const response = await apiClient.queryRange(LCP_GRAFANA_QUERY, LCP_FETCH_START_UNIX, nowUnix, LCP_STEP);
      const result = response?.data?.result;
      if (result && result.length > 0 && result[0].values) {
        setLcpGrafanaValues(result[0].values as Array<[number, string]>);
      } else {
        setLcpGrafanaValues([]);
      }
    } catch (e) {
      console.error('Failed to fetch Statuspage LCP from Grafana', e);
      setLcpGrafanaValues([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(`${API_BASE_URL}/api/page0-metrics?year=2026`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      setData(json);
      setLastRefreshed(new Date());
    } catch (err: any) {
      setError(err?.message || 'Failed to load Page 0 metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchLcpData();
  }, []);

  const weeks: WeekData[] = data?.weeks ?? [];

  const [periodType, setPeriodType] = useState<'weekly'|'monthly'|'quarterly'|'yearly'>('weekly');
  const [timeRange, setTimeRange] = useState<string>('ytd');
  const KPI_OPTIONS = [
    'P1/P2 INC Count',
    'MTTR (avg hrs)',
    'MTTD (avg hrs)',
    'Capture by Alert %',
    'Statuspage Capture %',
    'INC raised by StatusPage %',
    'Statuspage LCP',
    'Caused by Change %',
    'Recurring INC',
    'Aging',
    'Full Stack %',
  ];
  const [selectedKPIs, setSelectedKPIs] = useState<string[]>([...KPI_OPTIONS]);

  // simple targets (numeric for comparisons)
  const TARGETS_BY_PERIOD: Record<string, any> = {
    weekly:    { aging: 15, recurring: 2, statuspage: 80, fullstack: 80, lcp: 2500, inc_count: 7, alert_capture: 80, change_caused: 20, mttd: 0.5, mttr: 3 },
    monthly:   { aging: 15, recurring: 2, statuspage: 80, fullstack: 80, lcp: 2500, inc_count: 7, alert_capture: 80, change_caused: 20, mttd: 0.5, mttr: 3 },
    quarterly: { aging: 15, recurring: 2, statuspage: 80, fullstack: 80, lcp: 2500, inc_count: 7, alert_capture: 80, change_caused: 20, mttd: 0.5, mttr: 3 },
    yearly:    { aging: 15, recurring: 2, statuspage: 80, fullstack: 80, lcp: 2500, inc_count: 7, alert_capture: 80, change_caused: 20, mttd: 0.5, mttr: 3 },
  };

  const TIME_RANGE_OPTIONS = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'ytd', label: 'Year to Date' },
  ];

  // Helpers for ISO periods
  const getISOWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };
  const getISOYear = (date: Date): number => {
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    return d.getUTCFullYear();
  };
  const getISOThursday = (date: Date): Date => {
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    return d;
  };
  const getPeriodKey = (date: Date, pType: string): string => {
    if (pType === 'weekly') {
      return `${getISOYear(date)}-W${String(getISOWeekNumber(date)).padStart(2, '0')}`;
    } else if (pType === 'monthly') {
      const thu = getISOThursday(date);
      return `${thu.getUTCFullYear()}-${String(thu.getUTCMonth() + 1).padStart(2, '0')}`;
    } else if (pType === 'quarterly') {
      const thu = getISOThursday(date);
      const q = Math.floor(thu.getUTCMonth() / 3) + 1;
      return `${thu.getUTCFullYear()}-Q${q}`;
    }
    return `${getISOYear(date)}`;
  };
  const formatPeriodLabel = (period: string, pType: string) => {
    if (pType === 'weekly') return period;
    if (pType === 'monthly') {
      const [y, m] = period.split('-');
      const d = new Date(parseInt(y), parseInt(m) - 1);
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    return period;
  };

  const getCellStyle = (kpi: string, actual: number | null) => {
    const targets = TARGETS_BY_PERIOD[periodType];
    if (actual === null || actual === undefined) return {};
    // On target: green background; Off target: red/pink background
    if (kpi === 'Aging') {
      return actual > targets.aging ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'Recurring INC') {
      // recurring target is <2, so treat >= target value as off-target
      return actual >= targets.recurring ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'Statuspage Capture %') {
      return actual < targets.statuspage ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
      }
      if (kpi === 'INC raised by StatusPage %') {
        return actual < targets.statuspage ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'Full Stack %') {
      return actual < targets.fullstack ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'Statuspage LCP') {
      // LCP in ms — lower is better; green if under target, red if over
      return actual > targets.lcp ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'P1/P2 INC Count') {
      // lower is better — green if <= target
      return actual > targets.inc_count ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'Capture by Alert %') {
      // higher is better — green if >= target
      return actual < targets.alert_capture ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'Caused by Change %') {
      return actual > targets.change_caused ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'MTTD (avg hrs)') {
      return actual > targets.mttd ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    if (kpi === 'MTTR (avg hrs)') {
      return actual > targets.mttr ? { backgroundColor: '#ffcdd2', color: '#c62828' } : { backgroundColor: '#dff0d8', color: '#2e7d32' };
    }
    return {};
  };

  // Aggregate weeks into display periods based on selected periodType
  const displayPeriods = useMemo(() => {
    if (!weeks || weeks.length === 0) return [];

    // Look up the LCP value (ms) for a given week date range from Grafana range query results
    const getLcpForWeek = (startDate: string | undefined, endDate: string | undefined): number | null => {
      if (!startDate || !endDate || lcpGrafanaValues.length === 0) return null;
      const wStart = new Date(startDate + 'T00:00:00Z').getTime() / 1000;
      const wEnd = new Date(endDate + 'T23:59:59Z').getTime() / 1000;
      const point = lcpGrafanaValues.find(([ts]) => ts >= wStart && ts <= wEnd);
      if (!point) return null;
      const v = parseFloat(point[1]);
      return isNaN(v) ? null : Math.round(v);
    };
    // apply timeRange -> compute start/end dates
    const end = new Date();
    let start = new Date(end);
    switch (timeRange) {
      case '7d': start.setDate(end.getDate() - 7); break;
      case '30d': start.setDate(end.getDate() - 30); break;
      case '90d': start.setDate(end.getDate() - 90); break;
      case 'ytd': start = new Date(Date.UTC(end.getFullYear(), 0, 1)); break;
      default: start.setDate(end.getDate() - 365); break;
    }

    // include weeks that overlap the selected date window (week.start_date..week.end_date)
    const filteredWeeks = weeks.filter(w => {
      if (!w.start_date || !w.end_date) return false;
      const ws = new Date(w.start_date + 'T00:00:00Z');
      const we = new Date(w.end_date + 'T23:59:59Z');
      return we >= start && ws <= end;
    });

    if (periodType === 'weekly') {
      return filteredWeeks.map(w => {
        const start = w.start_date ? w.start_date.substring(0,10) : undefined;
        const endd = w.end_date ? w.end_date.substring(0,10) : undefined;
        const isoYear = start ? getISOYear(new Date(start+'T00:00:00Z')) : new Date().getFullYear();
        const label = `W${String(w.week).replace(/^0+/,'')} - ${isoYear}`;
        // Statuspage cutoff: only show data from Mar 11, 2026 onwards
        const weekEnd = w.end_date ? new Date(w.end_date + 'T00:00:00Z') : null;
        const weekStart = w.start_date ? new Date(w.start_date + 'T00:00:00Z') : null;
        let spPct: number | null = w.status_page_pct;
        let spNa = false;
        if (weekEnd && weekEnd < SP_CUTOFF) {
          // Whole week precedes cutoff → NA
          spPct = null;
          spNa = true;
        } else if (weekStart && weekStart < SP_CUTOFF && Array.isArray(w.status_page_incidents) && w.status_page_incidents.length > 0) {
          // Week straddles the cutoff → recompute from incidents on/after Mar 11 only
          const spFiltered = w.status_page_incidents.filter(i => i.created_on && new Date(i.created_on) >= SP_CUTOFF);
          spPct = spFiltered.length > 0 ? +((spFiltered.filter(i => i.captured).length / spFiltered.length) * 100).toFixed(1) : null;
        }
        // Raised-by-StatusPage KPI: only show from ISO week 16 onward for 2026 (earlier weeks -> NA)
        const RAISED_MIN_YEAR = 2026;
        const RAISED_MIN_WEEK = 16;
        const raiseNa = (isoYear < RAISED_MIN_YEAR) || (isoYear === RAISED_MIN_YEAR && (w.week ?? 0) < RAISED_MIN_WEEK);
        const raisedPct = raiseNa ? null : (w.inc_raised_by_statuspage_pct ?? null);
        const weekLcp = getLcpForWeek(w.start_date, w.end_date);
        return { key: `${w.week_label}-${isoYear}`, label, incident_count: w.incident_count, recurrence_count: w.recurrence_count, avg_aging_days: w.avg_aging_days, status_page_pct: spPct, statuspage_na: spNa, rf_fullstack_pct: w.rf_fullstack_pct ?? null, inc_raised_by_statuspage_pct: raisedPct, inc_raised_by_statuspage_count: w.inc_raised_by_statuspage_count ?? 0, raised_na: raiseNa, lcp_ms: weekLcp, alert_capture_pct: w.alert_capture_pct ?? null, alert_capture_count: w.alert_capture_count ?? 0, caused_by_change_pct: w.caused_by_change_pct ?? null, caused_by_change_count: w.caused_by_change_count ?? 0, avg_mttd_hrs: w.avg_mttd_hrs ?? null, avg_mttr_hrs: w.avg_mttr_hrs ?? null, all_wk_incidents: w.all_wk_incidents ?? [], start_date: start, end_date: endd, _sourceWeeks: [w] };
      });
    }

    // For non-weekly aggregation: accumulate per period key
    const map: Record<string, any> = {};
    filteredWeeks.forEach(w => {
      const dt = new Date((w.start_date || w.end_date) + 'T00:00:00Z');
      const key = getPeriodKey(dt, periodType);
      if (!map[key]) map[key] = { key, label: key, incident_count: 0, recurrence_count: 0, latestWeekDate: null, latestAging: null, latestFullstackDate: null, latestFullstack: null, status_incidents: 0, status_captured: 0, sp_has_data: false, lcpSum: 0, lcpCount: 0, alert_capture_count: 0, caused_by_change_count: 0, mttdSum: 0, mttdCount: 0, mttrSum: 0, mttrCount: 0, _sourceWeeks: [] as WeekData[] };
      map[key].incident_count += (w.incident_count || 0);
      map[key].recurrence_count += (w.recurrence_count || 0);
      map[key]._sourceWeeks.push(w);
      // capture latest week's aging (use end_date to determine latest)
      if (w.avg_aging_days !== null && w.avg_aging_days !== undefined) {
        const wkEnd = new Date(w.end_date + 'T00:00:00Z');
        if (!map[key].latestWeekDate || wkEnd > map[key].latestWeekDate) {
          map[key].latestWeekDate = wkEnd;
          map[key].latestAging = w.avg_aging_days;
        }
      }
      // capture latest week's fullstack % using its own date tracker
      if (w.rf_fullstack_pct !== null && w.rf_fullstack_pct !== undefined) {
        const wkEndFs = new Date(w.end_date + 'T00:00:00Z');
        if (!map[key].latestFullstackDate || wkEndFs > map[key].latestFullstackDate) {
          map[key].latestFullstackDate = wkEndFs;
          map[key].latestFullstack = w.rf_fullstack_pct;
        }
      }
      // status page incidents totals — only count incidents on/after Mar 11, 2026
      const wkEndForSp = w.end_date ? new Date(w.end_date + 'T00:00:00Z') : null;
      if (wkEndForSp && wkEndForSp >= SP_CUTOFF) {
        map[key].sp_has_data = true;
        if (Array.isArray(w.status_page_incidents)) {
          const spFiltered = w.status_page_incidents.filter(i => i.created_on && new Date(i.created_on) >= SP_CUTOFF);
          map[key].status_incidents += spFiltered.length;
          map[key].status_captured += spFiltered.filter(i => i.captured).length;
        }
        // aggregate INC raised-by-StatusPage counts if backend provided them per-week
        // Only include weeks that meet the ISO week cutoff (week 16 of 2026 onwards)
        const weekYear = w.start_date ? getISOYear(new Date(w.start_date + 'T00:00:00Z')) : getISOYear(new Date(w.end_date + 'T00:00:00Z'));
        const weekNum = w.week ?? 0;
        const includeRaised = !(weekYear < 2026 || (weekYear === 2026 && weekNum < 16));
        if (includeRaised) {
          map[key].inc_raised_count = (map[key].inc_raised_count || 0) + (w.inc_raised_by_statuspage_count ?? 0);
          map[key].raised_has_data = true;
        }
      }
      // Accumulate LCP for averaging across the aggregated period
      const weekLcpV = getLcpForWeek(w.start_date, w.end_date);
      if (weekLcpV !== null) {
        map[key].lcpSum += weekLcpV;
        map[key].lcpCount += 1;
      }
      // Accumulate alert capture and caused-by-change counts
      map[key].alert_capture_count += (w.alert_capture_count ?? 0);
      map[key].caused_by_change_count += (w.caused_by_change_count ?? 0);
      // Accumulate MTTD/MTTR totals for averaging
      if (w.avg_mttd_hrs !== null && w.avg_mttd_hrs !== undefined) {
        map[key].mttdSum += w.avg_mttd_hrs;
        map[key].mttdCount += 1;
      }
      if (w.avg_mttr_hrs !== null && w.avg_mttr_hrs !== undefined) {
        map[key].mttrSum += w.avg_mttr_hrs;
        map[key].mttrCount += 1;
      }
    });

    return Object.values(map).map((v: any) => ({
      key: v.key,
      label: formatPeriodLabel(v.label, periodType),
      incident_count: v.incident_count,
      recurrence_count: v.recurrence_count,
      // per requirements: aging = latest week's value in the period
      avg_aging_days: v.latestAging !== null ? v.latestAging : null,
      // full stack % = latest week's value in the period when available
      rf_fullstack_pct: v.latestFullstack !== null ? v.latestFullstack : null,
      // statuspage % = total captured / total incidents * 100 (only from Mar 11+)
      status_page_pct: v.status_incidents > 0 ? +((v.status_captured / v.status_incidents) * 100).toFixed(1) : null,
      // INC raised by StatusPage % = summed raised count / total incidents * 100
      inc_raised_by_statuspage_pct: v.raised_has_data && v.inc_raised_count > 0 && v.incident_count > 0 ? +((v.inc_raised_count / v.incident_count) * 100).toFixed(1) : null,
      inc_raised_by_statuspage_count: v.inc_raised_count || 0,
      statuspage_na: !v.sp_has_data,
      raised_na: !v.raised_has_data,
      // LCP = average of weekly values within the period
      lcp_ms: v.lcpCount > 0 ? Math.round(v.lcpSum / v.lcpCount) : null,
      // Alert capture % = total alert-captured / total incidents * 100
      alert_capture_count: v.alert_capture_count,
      alert_capture_pct: v.incident_count > 0 ? +((v.alert_capture_count / v.incident_count) * 100).toFixed(1) : null,
      // Caused-by-change % = total caused-by-change / total incidents * 100
      caused_by_change_count: v.caused_by_change_count,
      caused_by_change_pct: v.incident_count > 0 ? +((v.caused_by_change_count / v.incident_count) * 100).toFixed(1) : null,
      avg_mttd_hrs: v.mttdCount > 0 ? +(v.mttdSum / v.mttdCount).toFixed(2) : null,
      avg_mttr_hrs: v.mttrCount > 0 ? +(v.mttrSum / v.mttrCount).toFixed(2) : null,
      all_wk_incidents: (v._sourceWeeks as WeekData[]).flatMap((sw: WeekData) => sw.all_wk_incidents ?? []),
      _sourceWeeks: v._sourceWeeks as WeekData[],
      start_date: (v._sourceWeeks as WeekData[])[0]?.start_date?.substring(0, 10) ?? undefined,
      end_date: (v._sourceWeeks as WeekData[])[(v._sourceWeeks as WeekData[]).length - 1]?.end_date?.substring(0, 10) ?? undefined,
    }));
  }, [weeks, periodType, timeRange, lcpGrafanaValues]);

  const handleCellClick = (k: string, period: any) => {
    const sourceWeeks: WeekData[] = period._sourceWeeks ?? [];
    if (sourceWeeks.length === 0) return;
    const firstWeek = sourceWeeks[0];
    const lastWeek = sourceWeeks[sourceWeeks.length - 1];
    if (k === 'Recurring INC') {
      const allPM = sourceWeeks.flatMap((sw: WeekData) => sw.postmortem_incidents ?? []);
      setPmDrillWeek({ ...firstWeek, week_label: period.label, start_date: firstWeek?.start_date ?? '', end_date: lastWeek?.end_date ?? '', postmortem_incidents: allPM } as WeekData);
    } else if (k === 'Statuspage Capture %') {
      const allSP = sourceWeeks
        .flatMap((sw: WeekData) => sw.status_page_incidents ?? [])
        .filter(i => i.created_on && new Date(i.created_on) >= SP_CUTOFF);
      setDrillWeek({ ...firstWeek, week_label: period.label, start_date: firstWeek?.start_date ?? '', end_date: lastWeek?.end_date ?? '', status_page_incidents: allSP, drill_mode: 'capture' } as WeekData);
    }
    else if (k === 'INC raised by StatusPage %') {
      // Gather incidents_this_week from the source weeks (same base as capture drill)
      const allIncidents: Array<SpIncident & { raised?: boolean; captured?: boolean }> = sourceWeeks.flatMap((sw: WeekData) => (sw.incidents_this_week ?? []));
      // filter by cutoff
      const allFiltered = allIncidents.filter((i: SpIncident & { raised?: boolean; captured?: boolean }) => i.created_on && new Date(i.created_on) >= SP_CUTOFF);
      setDrillWeek({ ...firstWeek, week_label: period.label, start_date: firstWeek?.start_date ?? '', end_date: lastWeek?.end_date ?? '', status_page_incidents: allFiltered, drill_mode: 'raised' } as WeekData);
    } else if (k === 'P1/P2 INC Count') {
      const allIncs: AllWkIncident[] = sourceWeeks.flatMap((sw: WeekData) => sw.all_wk_incidents ?? []);
      setIncDrillData({ label: period.label, start_date: firstWeek?.start_date ?? '', end_date: lastWeek?.end_date ?? '', mode: 'inc_count', incidents: allIncs });
    } else if (k === 'Capture by Alert %') {
      const allIncs: AllWkIncident[] = sourceWeeks.flatMap((sw: WeekData) => sw.all_wk_incidents ?? []);
      setIncDrillData({ label: period.label, start_date: firstWeek?.start_date ?? '', end_date: lastWeek?.end_date ?? '', mode: 'alert_capture', incidents: allIncs });
    } else if (k === 'Caused by Change %') {
      const allIncs: AllWkIncident[] = sourceWeeks.flatMap((sw: WeekData) => sw.all_wk_incidents ?? []);
      setIncDrillData({ label: period.label, start_date: firstWeek?.start_date ?? '', end_date: lastWeek?.end_date ?? '', mode: 'caused_by_change', incidents: allIncs });
    } else if (k === 'MTTD (avg hrs)') {
      const allIncs: AllWkIncident[] = sourceWeeks.flatMap((sw: WeekData) => sw.all_wk_incidents ?? []);
      setIncDrillData({ label: period.label, start_date: firstWeek?.start_date ?? '', end_date: lastWeek?.end_date ?? '', mode: 'mttd', incidents: allIncs });
    } else if (k === 'MTTR (avg hrs)') {
      const allIncs: AllWkIncident[] = sourceWeeks.flatMap((sw: WeekData) => sw.all_wk_incidents ?? []);
      setIncDrillData({ label: period.label, start_date: firstWeek?.start_date ?? '', end_date: lastWeek?.end_date ?? '', mode: 'mttr', incidents: allIncs });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="primary" />
          SRE Page 0 Metrics
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastRefreshed && (
            <Typography variant="caption" color="text.secondary">Last updated: {lastRefreshed.toLocaleTimeString()}</Typography>
          )}
          <Tooltip title="Refresh"><span><IconButton onClick={() => { fetchData(); fetchLcpData(); }} size="small"><Refresh /></IconButton></span></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {!loading && weeks.length === 0 && (
        <Alert severity="info">No data available for the selected period.</Alert>
      )}

      {!loading && weeks.length > 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Period Type</InputLabel>
              <Select value={periodType} label="Period Type" onChange={(e) => setPeriodType(e.target.value as any)}>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Time Range</InputLabel>
              <Select value={timeRange} label="Time Range" onChange={(e) => setTimeRange(e.target.value as any)}>
                {TIME_RANGE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 300 }}>
              <InputLabel>KPI</InputLabel>
              <Select
                multiple
                value={selectedKPIs}
                onChange={(e) => setSelectedKPIs(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))}
                input={<OutlinedInput label="KPI" />}
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {KPI_OPTIONS.map((k) => (
                  <MenuItem key={k} value={k}>
                    <Checkbox checked={selectedKPIs.indexOf(k) > -1} />
                    <ListItemText primary={k} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <Table size="small" sx={{ borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  {/* "Timeline" header spans the KPI-name column + the sub-row label column */}
                  <TableCell
                    colSpan={2}
                    sx={{ fontWeight: 700, fontSize: '0.85rem', backgroundColor: '#cfe8fc', border: '1px solid #b0c4de', textAlign: 'center' }}
                  >
                    Timeline
                  </TableCell>
                  {displayPeriods.map((w) => (
                    <TableCell
                      key={`hdr-${w.key}`}
                      sx={{ textAlign: 'center', fontWeight: 700, fontSize: '0.8rem', backgroundColor: '#cfe8fc', border: '1px solid #b0c4de', whiteSpace: 'nowrap' }}
                    >
                      {periodType === 'weekly' && w.start_date && w.end_date ? (
                        <Tooltip title={`${w.start_date} – ${w.end_date}`} arrow>
                          <span style={{ cursor: 'default' }}>{w.label}</span>
                        </Tooltip>
                      ) : w.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedKPIs.map((kpi, kpiIdx) => {
                  const targets = TARGETS_BY_PERIOD[periodType];
                  const targetLabel = kpi === 'Aging'
                    ? `<= ${targets.aging}`
                    : kpi === 'Recurring INC'
                    ? `< ${targets.recurring}`
                    : kpi === 'Statuspage Capture %'
                    ? `>= ${targets.statuspage}%`
                    : kpi === 'Statuspage LCP'
                    ? `< ${targets.lcp}ms`
                    : kpi === 'P1/P2 INC Count'
                    ? `<= ${targets.inc_count}`
                    : kpi === 'Capture by Alert %'
                    ? `>= ${targets.alert_capture}%`
                    : kpi === 'Caused by Change %'
                    ? `<= ${targets.change_caused}%`
                    : kpi === 'MTTD (avg hrs)'
                    ? `<= ${targets.mttd}hr`
                    : kpi === 'MTTR (avg hrs)'
                    ? `<= ${targets.mttr}hr`
                    : `>= ${targets.fullstack}%`;

                  const rowBg = kpiIdx % 2 === 0 ? '#f0f7ff' : '#ffffff';

                  return (
                    <React.Fragment key={`kpi-${kpi}`}>
                      {/* TARGET sub-row */}
                      <TableRow>
                        {/* KPI name cell spans 2 rows (Target + ACTUAL) */}
                        <TableCell
                          rowSpan={2}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            backgroundColor: rowBg,
                            border: '1px solid #d0d7de',
                            minWidth: 130,
                            verticalAlign: 'middle',
                          }}
                        >
                          {kpi}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            backgroundColor: '#dbeafe',
                            border: '1px solid #d0d7de',
                            whiteSpace: 'nowrap',
                            color: '#1e40af',
                          }}
                        >
                          Target
                        </TableCell>
                        {displayPeriods.map((w) => (
                          <TableCell
                            key={`tgt-${kpi}-${w.key}`}
                            sx={{
                              textAlign: 'center',
                              fontSize: '0.8rem',
                              backgroundColor: '#dbeafe',
                              border: '1px solid #d0d7de',
                              color: '#1e40af',
                            }}
                          >
                            {targetLabel}
                          </TableCell>
                        ))}
                      </TableRow>

                      {/* ACTUAL sub-row */}
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            backgroundColor: rowBg,
                            border: '1px solid #d0d7de',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ACTUAL
                        </TableCell>
                        {displayPeriods.map((w) => {
                          const statuspageNa = kpi === 'Statuspage Capture %' && w.statuspage_na;
                          const raisedNa = kpi === 'INC raised by StatusPage %' && (w.raised_na ?? false);
                          const val = kpi === 'Aging'
                            ? w.avg_aging_days
                            : kpi === 'Recurring INC'
                            ? w.recurrence_count
                            : kpi === 'Statuspage Capture %'
                            ? (statuspageNa ? null : w.status_page_pct)
                            : kpi === 'INC raised by StatusPage %'
                            ? (raisedNa ? null : w.inc_raised_by_statuspage_pct)
                            : kpi === 'Statuspage LCP'
                            ? w.lcp_ms
                            : kpi === 'P1/P2 INC Count'
                            ? w.incident_count
                            : kpi === 'Capture by Alert %'
                            ? w.alert_capture_pct
                            : kpi === 'Caused by Change %'
                            ? w.caused_by_change_pct
                            : kpi === 'MTTD (avg hrs)'
                            ? w.avg_mttd_hrs
                            : kpi === 'MTTR (avg hrs)'
                            ? w.avg_mttr_hrs
                            : w.rf_fullstack_pct;
                          const cellStyle = (statuspageNa || raisedNa) ? {} : getCellStyle(kpi, val);
                          const isClickable = kpi === 'Recurring INC' || (kpi === 'Statuspage Capture %' && !statuspageNa) || (kpi === 'INC raised by StatusPage %' && !raisedNa) || kpi === 'P1/P2 INC Count' || kpi === 'Capture by Alert %' || kpi === 'Caused by Change %' || kpi === 'MTTD (avg hrs)' || kpi === 'MTTR (avg hrs)';
                          return (
                            <TableCell
                              key={`act-${kpi}-${w.key}`}
                              sx={{
                                textAlign: 'center',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                border: '1px solid #d0d7de',
                                ...cellStyle,
                                ...(isClickable ? { cursor: 'pointer' } : {}),
                              }}
                              onClick={isClickable ? () => handleCellClick(kpi, w) : undefined}
                            >
                              {kpi === 'Aging' && <ValueChip value={w.avg_aging_days} suffix="d" />}
                              {kpi === 'Recurring INC' && (
                                <Tooltip title="Click to view recurring incidents" arrow>
                                  <span><ValueChip value={w.recurrence_count} /></span>
                                </Tooltip>
                              )}
                              {kpi === 'Statuspage Capture %' && (
                                statuspageNa
                                  ? <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#9e9e9e' }}>NA</Typography>
                                  : <Tooltip title="Click to view status page incidents" arrow>
                                      <span><ValueChip value={w.status_page_pct} suffix="%" /></span>
                                    </Tooltip>
                              )}
                                {kpi === 'INC raised by StatusPage %' && (
                                  raisedNa
                                    ? <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#9e9e9e' }}>NA</Typography>
                                    : <Tooltip title="INCs raised by StatusPage (percent)" arrow>
                                        <span><ValueChip value={w.inc_raised_by_statuspage_pct} suffix="%" /></span>
                                      </Tooltip>
                                )}
                              {kpi === 'Full Stack %' && <ValueChip value={w.rf_fullstack_pct} suffix="%" />}
                              {kpi === 'Statuspage LCP' && (
                                w.lcp_ms === null
                                  ? <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#9e9e9e' }}>NA</Typography>
                                  : <Tooltip title="LCP p75 (ms) — target < 2500ms" arrow>
                                      <span><ValueChip value={w.lcp_ms} suffix="ms" /></span>
                                    </Tooltip>
                              )}
                              {kpi === 'P1/P2 INC Count' && (
                                <Tooltip title="Click to view incidents" arrow>
                                  <span><ValueChip value={w.incident_count} /></span>
                                </Tooltip>
                              )}
                              {kpi === 'Capture by Alert %' && (
                                w.alert_capture_pct === null || w.alert_capture_pct === undefined
                                  ? <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#9e9e9e' }}>NA</Typography>
                                  : <Tooltip title="Click to view alert-captured incidents" arrow>
                                      <span><ValueChip value={w.alert_capture_pct} suffix="%" /></span>
                                    </Tooltip>
                              )}
                              {kpi === 'Caused by Change %' && (
                                w.caused_by_change_pct === null || w.caused_by_change_pct === undefined
                                  ? <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#9e9e9e' }}>NA</Typography>
                                  : <Tooltip title="Click to view change-caused incidents" arrow>
                                      <span><ValueChip value={w.caused_by_change_pct} suffix="%" /></span>
                                    </Tooltip>
                              )}
                              {kpi === 'MTTD (avg hrs)' && (
                                w.avg_mttd_hrs === null || w.avg_mttd_hrs === undefined
                                  ? <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#9e9e9e' }}>NA</Typography>
                                  : <Tooltip title="Click to view MTTD per incident" arrow>
                                      <span><ValueChip value={w.avg_mttd_hrs} suffix="hr" /></span>
                                    </Tooltip>
                              )}
                              {kpi === 'MTTR (avg hrs)' && (
                                w.avg_mttr_hrs === null || w.avg_mttr_hrs === undefined
                                  ? <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#9e9e9e' }}>NA</Typography>
                                  : <Tooltip title="Click to view MTTR per incident" arrow>
                                      <span><ValueChip value={w.avg_mttr_hrs} suffix="hr" /></span>
                                    </Tooltip>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }} />
              <Typography variant="body2">Plan Targets</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#dff0d8', border: '1px solid #a5d6a7' }} />
              <Typography variant="body2">On Target</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#ffcdd2', border: '1px solid #ef9a9a' }} />
              <Typography variant="body2">Off Target</Typography>
            </Box>
          </Box>
        </>
      )}

      {/* Status Page drill-down modal (reused from previous implementation) */}
      <Dialog open={!!drillWeek} onClose={() => setDrillWeek(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#001a35', color: '#fff', fontWeight: 700 }}>
          {drillWeek?.drill_mode === 'raised' ? `INC Raised by StatusPage — ${drillWeek?.week_label}` : `Status Page Capture — ${drillWeek?.week_label}`} ({drillWeek?.start_date} – {drillWeek?.end_date})
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {drillWeek && drillWeek.status_page_incidents.length === 0 ? (
            <Box sx={{ p: 3 }}><Alert severity="info">No P1/P2 incidents created in this week.</Alert></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Incident</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>App ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>App Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created On</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Resolved At</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Status Page</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drillWeek?.status_page_incidents.map((inc) => (
                    <TableRow key={inc.number} hover>
                      <TableCell sx={{ fontWeight: 600, color: '#0067b3' }}>{inc.number}</TableCell>
                      <TableCell>{inc.app_id}</TableCell>
                      <TableCell>{inc.app_name || '—'}</TableCell>
                      <TableCell>{inc.created_on ? new Date(inc.created_on).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</TableCell>
                      <TableCell>{inc.resolved_at ? new Date(inc.resolved_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : 'Open'}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {drillWeek?.drill_mode === 'capture' ? (
                          inc.captured ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22 }} /> : <CancelIcon sx={{ color: '#c62828', fontSize: 22 }} />
                        ) : (
                          // in 'raised' mode, show tick if incident was raised by StatusPage, else cross
                          (inc as any).raised ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22 }} /> : <CancelIcon sx={{ color: '#c62828', fontSize: 22 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Typography variant="caption" color="text.secondary">
            {drillWeek && drillWeek.drill_mode === 'capture' && `${drillWeek.status_page_incidents.filter(i => i.captured).length} / ${drillWeek.status_page_incidents.length} incidents captured`}
            {drillWeek && drillWeek.drill_mode === 'raised' && `${drillWeek.status_page_incidents.length} incidents raised by StatusPage`}
          </Typography>
          <Button onClick={() => setDrillWeek(null)} variant="contained" size="small" sx={{ backgroundColor: '#001a35' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* INC Count / Alert Capture / Caused by Change drill modal */}
      <Dialog open={!!incDrillData} onClose={() => setIncDrillData(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#001a35', color: '#fff', fontWeight: 700 }}>
          {incDrillData?.mode === 'inc_count' && `P1/P2 INC Count — ${incDrillData?.label}`}
          {incDrillData?.mode === 'alert_capture' && `Capture by Alert — ${incDrillData?.label}`}
          {incDrillData?.mode === 'caused_by_change' && `Caused by Change — ${incDrillData?.label}`}
          {incDrillData?.mode === 'mttd' && `MTTD (avg hrs) — ${incDrillData?.label}`}
          {incDrillData?.mode === 'mttr' && `MTTR (avg hrs) — ${incDrillData?.label}`}
          {incDrillData && ` (${incDrillData.start_date} – ${incDrillData.end_date})`}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {incDrillData && incDrillData.incidents.length === 0 ? (
            <Box sx={{ p: 3 }}><Alert severity="info">No P1/P2 incidents in this period.</Alert></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Incident</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>App Name</TableCell>
                    {incDrillData?.mode !== 'inc_count' && incDrillData?.mode !== 'mttd' && incDrillData?.mode !== 'mttr' && <TableCell sx={{ fontWeight: 700 }}>Contact Type</TableCell>}
                    {incDrillData?.mode !== 'inc_count' && incDrillData?.mode !== 'mttd' && incDrillData?.mode !== 'mttr' && <TableCell sx={{ fontWeight: 700 }}>Caused By</TableCell>}
                    {(incDrillData?.mode === 'mttd' || incDrillData?.mode === 'mttr') && <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>TTD (hrs)</TableCell>}
                    {(incDrillData?.mode === 'mttd' || incDrillData?.mode === 'mttr') && <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>TTR (hrs)</TableCell>}
                    <TableCell sx={{ fontWeight: 700 }}>Created On</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Resolved At</TableCell>
                    {incDrillData?.mode === 'alert_capture' && <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Alert?</TableCell>}
                    {incDrillData?.mode === 'caused_by_change' && <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Change?</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incDrillData?.incidents.map((inc, idx) => (
                    <TableRow key={`${inc.number}-${idx}`} hover>
                      <TableCell sx={{ fontWeight: 600, color: '#0067b3' }}>{inc.number}</TableCell>
                      <TableCell>{inc.app_name || '—'}</TableCell>
                      {incDrillData?.mode !== 'inc_count' && incDrillData?.mode !== 'mttd' && incDrillData?.mode !== 'mttr' && <TableCell>{inc.contact_type || '—'}</TableCell>}
                      {incDrillData?.mode !== 'inc_count' && incDrillData?.mode !== 'mttd' && incDrillData?.mode !== 'mttr' && <TableCell>{inc.caused_by || '—'}</TableCell>}
                      {(incDrillData?.mode === 'mttd' || incDrillData?.mode === 'mttr') && (
                        <TableCell sx={{ textAlign: 'right' }}>
                          {inc.ttd_seconds != null ? (inc.ttd_seconds / 3600).toFixed(2) : '—'}
                        </TableCell>
                      )}
                      {(incDrillData?.mode === 'mttd' || incDrillData?.mode === 'mttr') && (
                        <TableCell sx={{ textAlign: 'right' }}>
                          {inc.ttr_seconds != null ? (inc.ttr_seconds / 3600).toFixed(2) : '—'}
                        </TableCell>
                      )}
                      <TableCell>{inc.created_on ? new Date(inc.created_on).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</TableCell>
                      <TableCell>{inc.resolved_at ? new Date(inc.resolved_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : 'Open'}</TableCell>
                      {incDrillData?.mode === 'alert_capture' && (
                        <TableCell sx={{ textAlign: 'center' }}>
                          {inc.alert_captured ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22 }} /> : <CancelIcon sx={{ color: '#c62828', fontSize: 22 }} />}
                        </TableCell>
                      )}
                      {incDrillData?.mode === 'caused_by_change' && (
                        <TableCell sx={{ textAlign: 'center' }}>
                          {inc.caused_by_change ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22 }} /> : <CancelIcon sx={{ color: '#c62828', fontSize: 22 }} />}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Typography variant="caption" color="text.secondary">
            {incDrillData?.mode === 'inc_count' && `${incDrillData.incidents.length} incidents`}
            {incDrillData?.mode === 'alert_capture' && `${incDrillData.incidents.filter(i => i.alert_captured).length} / ${incDrillData.incidents.length} captured by alert`}
            {incDrillData?.mode === 'caused_by_change' && `${incDrillData.incidents.filter(i => i.caused_by_change).length} / ${incDrillData.incidents.length} caused by change`}
            {incDrillData?.mode === 'mttd' && (() => { const vals = incDrillData.incidents.filter(i => i.ttd_seconds != null).map(i => i.ttd_seconds! / 3600); return vals.length > 0 ? `Avg MTTD: ${(vals.reduce((a,b) => a+b,0)/vals.length).toFixed(2)}hr (${vals.length} incidents with data)` : 'No MTTD data'; })()}
            {incDrillData?.mode === 'mttr' && (() => { const vals = incDrillData.incidents.filter(i => i.ttr_seconds != null).map(i => i.ttr_seconds! / 3600); return vals.length > 0 ? `Avg MTTR: ${(vals.reduce((a,b) => a+b,0)/vals.length).toFixed(2)}hr (${vals.length} incidents with data)` : 'No MTTR data'; })()}
          </Typography>
          <Button onClick={() => setIncDrillData(null)} variant="contained" size="small" sx={{ backgroundColor: '#001a35' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Postmortem drill-down modal */}
      <Dialog open={!!pmDrillWeek} onClose={() => setPmDrillWeek(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#001a35', color: '#fff', fontWeight: 700 }}>
          Postmortem Recurrence — {pmDrillWeek?.week_label} ({pmDrillWeek?.start_date} – {pmDrillWeek?.end_date})
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {pmDrillWeek && (!pmDrillWeek.postmortem_incidents || pmDrillWeek.postmortem_incidents.length === 0) ? (
            <Box sx={{ p: 3 }}><Alert severity="info">No postmortem incidents in this week.</Alert></Box>
          ) : (
            pmRecurringRecs.length === 0 ? (
              <Box sx={{ p: 3 }}><Alert severity="info">No recurring postmortem incidents in this week.</Alert></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Incident ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Earlier Incident</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pmRecurringRecs.map((pm, idx) => {
                      const rawCurrent = pm.incident_id ? String(pm.incident_id) : null;
                      const rawEarlier = pm.recurrence?.incidentId ? String(pm.recurrence.incidentId) : null;
                      const extractId = (s: string | null) => {
                        if (!s) return null;
                        const m1 = s.match(/(INC\d{4,})/i);
                        if (m1) return m1[1].toUpperCase();
                        const m2 = s.match(/sysparm_query=number=([^&\s]+)/i);
                        if (m2) return m2[1].toUpperCase();
                        return s;
                      };
                      const currentLabel = extractId(rawCurrent) || '—';
                      const earlierLabel = extractId(rawEarlier) || '—';
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{currentLabel}</TableCell>
                          <TableCell>{earlierLabel}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', px: 3 }}>
          <Button onClick={() => setPmDrillWeek(null)} variant="contained" size="small" sx={{ backgroundColor: '#001a35' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SrePage0Metrics;
